import React, { useState, useRef, useEffect } from "react";
import { Send } from "lucide-react";
import { PageHeader, ChatBubble, TypingIndicator, nowTime, fmtTime } from "../components/Shared";
import { translations } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 5000;

export default function OperatorChat({ guest, navigate, lang }) {
  const T = translations[lang];
  const fullName = `${guest.title} ${guest.name}`;
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);
  const scrollRef = useRef(null);
  const typingRef = useRef(false);

  useEffect(() => {
    typingRef.current = typing;
  }, [typing]);

  const fetchHistory = async () => {
    const welcome = { role: "operator", text: T.opWelcome(fullName, guest.room), timestamp: nowTime() };
    try {
      const res = await fetch(`${API}/operator/history/${guest.sessionId}`);
      const hist = await res.json();
      if (Array.isArray(hist) && hist.length > 0) {
        const mapped = hist.map((h) => ({
          role: h.role,
          text: h.text,
          timestamp: fmtTime(h.timestamp),
          meta: h.role === "user" ? `${fullName} · ${T.staffRoom} ${guest.room}` : undefined,
        }));
        setMessages([welcome, ...mapped]);
      } else {
        setMessages([welcome]);
      }
    } catch {
      setMessages((cur) => (cur.length ? cur : [welcome]));
    }
  };

  useEffect(() => {
    fetchHistory();
    // eslint-disable-next-line
  }, [guest.sessionId, lang]);

  // Poll for staff replies that may arrive while this page stays open.
  // Skipped while the simulated auto-reply "typing" delay is in flight, so the
  // poll can't reveal the reply early and cause the setTimeout below to append
  // a duplicate bubble.
  useEffect(() => {
    const id = setInterval(() => {
      if (!typingRef.current) fetchHistory();
    }, POLL_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [guest.sessionId, lang]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, typing]);

  const send = async () => {
    const text = input.trim();
    if (!text || typing) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text, timestamp: nowTime(), meta: `${fullName} · ${T.staffRoom} ${guest.room}` }]);
    setTyping(true);

    try {
      const res = await fetch(`${API}/operator/message`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: guest.sessionId,
          message: text,
          guest_name: fullName,
          room_number: guest.room,
          lang,
        }),
      });
      const data = await res.json();
      const delay = 2000 + Math.random() * 1000;
      setTimeout(() => {
        setTyping(false);
        setMessages((m) => [...m, { role: "operator", text: data.reply, timestamp: nowTime() }]);
      }, delay);
    } catch (e) {
      setTyping(false);
      setMessages((m) => [...m, { role: "operator", text: T.aiError, timestamp: nowTime() }]);
    }
  };

  return (
    <div className="flex flex-col h-screen" data-testid="operator-chat-screen">
      <PageHeader
        title={T.opTitle}
        onBack={() => navigate("dashboard")}
        subtitle={
          <span className="inline-flex items-center gap-1.5 text-[12px] mt-0.5">
            <span className="w-2 h-2 rounded-full bg-[#2EAF7D]" /> {T.online}
          </span>
        }
      />

      <div ref={scrollRef} className="flex-1 overflow-y-auto hide-scrollbar bg-[#F7FAFC] px-4 py-4 flex flex-col gap-3">
        {messages.map((m, i) => (
          <ChatBubble key={i} role={m.role} text={m.text} timestamp={m.timestamp} meta={m.meta} />
        ))}
        {typing && <TypingIndicator label={T.opTyping} />}
      </div>

      <div className="bg-white border-t border-[#F0F7FF] px-3 py-3 pb-[calc(76px+env(safe-area-inset-bottom,0px))]">
        <div className="flex items-center gap-2">
          <input
            data-testid="operator-chat-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder={T.opPlaceholder}
            className="flex-1 bg-[#F0F7FF] border border-[#E0E8F0] rounded-full h-[46px] px-4 text-[15px] text-[#1A1A2E] placeholder-[#6B7B8D] focus:outline-none focus:border-[#4AADDF] focus:ring-[3px] focus:ring-[#4AADDF]/15 transition-all"
          />
          <button
            data-testid="operator-send-button"
            onClick={send}
            disabled={!input.trim() || typing}
            className="w-[46px] h-[46px] rounded-full bg-[#4AADDF] text-white flex items-center justify-center shrink-0 active:scale-90 transition-transform disabled:opacity-50"
          >
            <Send size={20} />
          </button>
        </div>
      </div>
    </div>
  );
}
